/**
 * TaskTableView component - Table-based view for tasks list
 * 
 * Displays tasks in a sortable table format with columns for:
 * - Task title and description
 * - Project, assignee, and milestone
 * - Status and priority badges
 * - Due date and overdue indicators
 * - Action buttons (View, Edit, Delete)
 */

import React from 'react';
import { Eye, Edit, Trash2, AlertCircle } from 'lucide-react';
import DataTable, { TableColumn } from './DataTable';
import Button from './Button';
import Badge from './Badge';
import { Task } from '../types/task';
import { Project } from '../types/project';
import { User } from '../types/user';
import { Milestone } from '../types/milestone';

interface TaskTableRow {
  id: string;
  title: string;
  description?: string;
  project: string;
  projectId: string;
  assignee: string;
  milestone: string;
  status: string;
  priority: string;
  dueDate?: string;
  isOverdue: boolean;
  daysOverdue?: number;
  createdAt: string;
}

interface TaskTableViewProps {
  tasks: Task[];
  projects: Project[];
  users: User[];
  milestones: Milestone[];
  onView: (taskId: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
}

const TaskTableView: React.FC<TaskTableViewProps> = ({
  tasks,
  projects,
  users,
  milestones,
  onView,
  onEdit,
  onDelete,
}) => {
  // Helper functions
  const getProjectName = (projectId: string): string => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown';
  };

  const getUserName = (userId?: string | null): string => {
    if (!userId) return 'Unassigned';
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.username) : 'Unknown';
  };

  const getMilestoneName = (milestoneId?: string | null): string => {
    if (!milestoneId) return 'No Milestone';
    const milestone = milestones.find(m => m.id === milestoneId);
    return milestone ? milestone.name : 'Unknown';
  };

  const calculateOverdue = (dueDate?: string | null, status?: string) => {
    if (!dueDate || status === 'completed') return { isOverdue: false, daysOverdue: 0 };
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = now.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { isOverdue: diffDays > 0, daysOverdue: diffDays };
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      todo: 'To Do',
      in_progress: 'In Progress',
      completed: 'Completed',
      on_hold: 'On Hold',
      pending: 'Pending',
    };
    
    const label = statusMap[status] || statusMap.todo;
    
    return (
      <Badge status={status as any} size="sm">
        {label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityMap: Record<string, string> = {
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    };
    
    const label = priorityMap[priority] || priorityMap.medium;
    
    return (
      <Badge priority={priority as any} size="sm">
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Transform tasks into table rows
  const tableData: TaskTableRow[] = tasks.map(task => {
    const overdueInfo = calculateOverdue(task.due_date, task.status);
    return {
      id: task.id,
      title: task.title,
      description: task.description ?? undefined,
      project: getProjectName(task.project_id),
      projectId: task.project_id,
      assignee: getUserName(task.assigned_to),
      milestone: getMilestoneName(task.milestone_id),
      status: task.status,
      priority: task.priority,
      dueDate: task.due_date ?? undefined,
      isOverdue: overdueInfo.isOverdue,
      daysOverdue: overdueInfo.daysOverdue,
      createdAt: task.created_at,
    };
  });

  // Define table columns
  const columns: TableColumn<TaskTableRow>[] = [
    {
      key: 'title',
      label: 'Task',
      sortable: true,
      className: 'font-medium text-gray-900 dark:text-white min-w-[250px]',
      render: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold">{row.title}</span>
            {row.isOverdue && (
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            )}
          </div>
          {row.description && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.project}</span>
      ),
    },
    {
      key: 'assignee',
      label: 'Assignee',
      sortable: true,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.assignee}</span>
      ),
    },
    {
      key: 'milestone',
      label: 'Milestone',
      sortable: true,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.milestone}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (row) => getPriorityBadge(row.priority),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      sortValue: (row) => row.dueDate || '',
      className: 'text-sm',
      render: (row) => (
        <div>
          <div className={`font-medium ${row.isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {formatDate(row.dueDate)}
          </div>
          {row.isOverdue && (
            <div className="text-xs text-red-600 dark:text-red-400 font-semibold">
              {row.daysOverdue} {row.daysOverdue === 1 ? 'day' : 'days'} overdue
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right min-w-[180px]',
      render: (row) => {
        const task = tasks.find(t => t.id === row.id)!;
        return (
          <div className="flex gap-1 justify-end">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Eye}
              onClick={() => onView(row.id)}
              title="View Details"
            >
              View
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={Edit}
              onClick={() => onEdit(task)}
              title="Edit Task"
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              leftIcon={Trash2}
              onClick={() => onDelete(row.id)}
              title="Delete Task"
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-xl shadow-lg p-6">
      <DataTable
        columns={columns}
        data={tableData}
        emptyMessage="No tasks found"
        striped={true}
        getRowKey={(row) => row.id}
        defaultSortKey="dueDate"
        defaultSortDirection="asc"
        onRowClick={(row) => onView(row.id)}
      />
    </div>
  );
};

export default TaskTableView;
